import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    CircularProgress,
    Alert,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { doc, onSnapshot, deleteDoc } from 'firebase/firestore'; // Import deleteDoc
import { db } from './firebaseConfig';

const WaitingForValet = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { conversationId: routeConversationId, isGuest: routeIsGuest, isRetrieval: routeIsRetrieval } =
        location.state || {};
    const storedGuestConversationId =
        sessionStorage.getItem('guestConversationId') ||
        localStorage.getItem('guestConversationId');
    const storedGuestUserRaw =
        sessionStorage.getItem('guestUser') || localStorage.getItem('guestUser');
    const storedIsRetrievalRaw =
        sessionStorage.getItem('guestIsRetrieval') ||
        localStorage.getItem('guestIsRetrieval');
    const isGuest =
        routeIsGuest ??
        (() => {
            try {
                return !!(storedGuestUserRaw && JSON.parse(storedGuestUserRaw));
            } catch (e) {
                return false;
            }
        })();
    const conversationId =
        routeConversationId || (isGuest ? storedGuestConversationId : undefined);
    const isRetrieval =
        routeIsRetrieval ??
        (isGuest && storedIsRetrievalRaw ? storedIsRetrievalRaw === 'true' : undefined);
    const [conversationStatus, setConversationStatus] = useState('pending');
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!conversationId) {
            setError('Conversation ID not provided.');
            return;
        }

        if (isGuest) {
            sessionStorage.setItem('guestConversationId', conversationId);
            localStorage.setItem('guestConversationId', conversationId);
        }

        if (isGuest && typeof isRetrieval === 'boolean') {
            sessionStorage.setItem('guestIsRetrieval', String(isRetrieval));
            localStorage.setItem('guestIsRetrieval', String(isRetrieval));
        }

        const unsubscribe = onSnapshot(
            doc(db, 'conversations', conversationId),
            (doc) => {
                if (doc.exists()) {
                    const data = doc.data();
                    setConversationStatus(data.status);

                    if (data.status === 'active') {
                        navigate('/active-chat', {
                            state: { 
                                conversationId,
                                isGuest,
                                valetId: data.valetId
                            },
                        });
                    }
                } else {
                    setError('Conversation not found.');
                }
            }
        );

        return () => unsubscribe();
    }, [conversationId, navigate, isGuest, isRetrieval]);

    const handleCancelRequest = async () => {
        try {
            // Delete the conversation document
            await deleteDoc(doc(db, 'conversations', conversationId));
            if (isGuest) {
                sessionStorage.removeItem('guestConversationId');
                sessionStorage.removeItem('guestIsRetrieval');
                localStorage.removeItem('guestConversationId');
                localStorage.removeItem('guestIsRetrieval');
            }
            navigate(isGuest ? '/' : '/home');
        } catch (err) {
            console.error('Error cancelling request:', err);
            setError('Failed to cancel request. Please try again.');
        }
    };

    if (error) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity='error'>{error}</Alert>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
            }}>
            <Box sx={{ textAlign: 'center' }}>
                <Typography variant='h4' sx={{ mb: 2 }}>
                    {isRetrieval 
                        ? 'Waiting for valet to accept your retrieval request...'
                        : 'Waiting for valet to accept your request...'}
                </Typography>
                <Typography variant='body1' sx={{ mb: 4, color: 'text.secondary' }}>
                    {isRetrieval 
                        ? 'Your valet will bring your car shortly'
                        : 'A valet will park your car soon'}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        mb: 2,
                    }}>
                    <CircularProgress />
                </Box>
                <Button
                    variant='contained'
                    color='error'
                    onClick={handleCancelRequest}>
                    Cancel Request
                </Button>
            </Box>
        </Box>
    );
};

export default WaitingForValet;
